import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, Lock, Bookmark, History as HistoryIcon, ChevronDown, ChevronRight as ChevronRightIcon, Eye, CalendarClock } from "lucide-react";

/**
 * Per-user, per-community feature-grant editor.
 *
 * Opens as a modal from the Users page. Renders the full feature catalog
 * grouped by category; each row has a Checkbox that is either:
 *   - locked ON (feature is part of the user's base role — cannot be revoked here)
 *   - locked OFF (caller doesn't hold that feature themselves; PM ceiling)
 *   - toggleable (an explicit grant the caller can flip)
 *
 * On save, POSTs the union of currently-checked toggleable features to
 * PUT /admin/permissions/users/{uid} for the currently-selected property.
 */
export function FeatureGrantsDialog({ user: targetUser, onClose, onSaved }) {
  const [catalog, setCatalog] = useState(null);        // {features, caller_role, caller_grantable_by_property}
  const [detail, setDetail] = useState(null);          // {user, base_role_features, grants_by_property, effective_by_property}
  const [propertyId, setPropertyId] = useState(null);
  const [selected, setSelected] = useState(new Set());  // currently-checked toggleable features
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState("");
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");   // yyyy-mm-dd; "" = never

  const load = useCallback(async () => {
    try {
      const [cat, det, tpl, hist] = await Promise.all([
        api.get("/admin/permissions/catalog"),
        api.get(`/admin/permissions/users/${targetUser.id}`),
        api.get("/admin/permissions/templates"),
        api.get(`/admin/permissions/history?user_id=${targetUser.id}&limit=8`),
      ]);
      setCatalog(cat.data);
      setDetail(det.data);
      setTemplates(tpl.data.items || []);
      setHistory(hist.data.items || []);
      // Prefer a property both target + caller share
      const targetProps = det.data.user.property_ids || [];
      const callerProps = Object.keys(cat.data.caller_grantable_by_property || {});
      const overlap = targetProps.find((p) => callerProps.includes(p));
      const initial = overlap || targetProps[0] || null;
      setPropertyId(initial);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load permissions");
    }
  }, [targetUser.id]);

  useEffect(() => { load(); }, [load]);

  // Seed `selected` from the existing grants + base-role features whenever
  // the property changes.
  useEffect(() => {
    if (!detail || !propertyId) return;
    const explicit = detail.grants_by_property?.[propertyId] || [];
    const base = new Set(detail.base_role_features || []);
    // Selected = base ∪ explicit (union) — the UI shows the *effective* state.
    const s = new Set([...base, ...explicit]);
    setSelected(s);
    // Seed expires_at from stored grant metadata (yyyy-mm-dd truncation for
    // <input type="date">). Empty = perma-grant.
    const meta = detail.granted_meta?.[propertyId];
    const raw = meta?.expires_at || "";
    setExpiresAt(raw ? String(raw).slice(0, 10) : "");
  }, [detail, propertyId]);

  const grantable = useMemo(() => {
    if (!catalog || !propertyId) return new Set();
    return new Set(catalog.caller_grantable_by_property?.[propertyId] || []);
  }, [catalog, propertyId]);

  const baseRoleSet = useMemo(
    () => new Set(detail?.base_role_features || []),
    [detail],
  );

  const grouped = useMemo(() => {
    if (!catalog) return {};
    const out = {};
    for (const f of catalog.features) {
      (out[f.category] ||= []).push(f);
    }
    return out;
  }, [catalog]);

  const toggle = (feature) => {
    if (baseRoleSet.has(feature.key)) return;     // locked ON
    if (!grantable.has(feature.key)) return;       // caller cannot grant
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(feature.key)) next.delete(feature.key);
      else next.add(feature.key);
      return next;
    });
  };

  const save = async () => {
    if (!propertyId) return;
    setBusy(true);
    try {
      // Backend filters out base-role features server-side, but we can
      // pass the full set — anything filtered is a no-op for that user.
      await api.put(`/admin/permissions/users/${targetUser.id}`, {
        property_id: propertyId,
        features: [...selected],
        expires_at: expiresAt || null,
      });
      toast.success(`Feature grants updated for ${targetUser.name || targetUser.email}`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to save grants");
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!propertyId) return;
    if (!window.confirm(`Revoke ALL extra feature grants for ${targetUser.name} on this community?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/permissions/users/${targetUser.id}`, { params: { property_id: propertyId } });
      toast.success("All extra grants revoked");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to revoke grants");
    } finally { setBusy(false); }
  };

  const applyTemplate = async (templateId) => {
    if (!templateId || !propertyId) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/permissions/users/${targetUser.id}/apply-template`,
                                       { template_id: templateId, property_id: propertyId, merge: true });
      toast.success(`Template "${data.template}" applied — ${data.features.length} feature${data.features.length === 1 ? "" : "s"} granted`);
      await load();          // refresh grants + history
      onSaved?.();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to apply template");
    } finally { setBusy(false); }
  };

  const saveAsTemplate = async () => {
    const name = saveTemplateName.trim();
    if (!name) return toast.error("Give this bundle a name first");
    // Only save the *deltas* — features beyond the target's base role
    const deltas = [...selected].filter((k) => !baseRoleSet.has(k));
    if (deltas.length === 0) return toast.error("No extra features to save — pick some first");
    setBusy(true);
    try {
      await api.post("/admin/permissions/templates", { name, features: deltas });
      toast.success(`Template "${name}" saved`);
      setSaveTemplateName("");
      setShowSaveTemplate(false);
      const { data } = await api.get("/admin/permissions/templates");
      setTemplates(data.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to save template");
    } finally { setBusy(false); }
  };

  const viewAsUser = async () => {
    // Only residents + vendors can be impersonated (backend enforces).
    if (!["resident", "vendor"].includes(targetUser.role)) {
      return toast.error(`Cannot impersonate ${targetUser.role} accounts (residents & vendors only)`);
    }
    if (!window.confirm(`Preview the app as ${targetUser.name || targetUser.email}? You'll be redirected — click "Exit impersonation" in the banner to return.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/impersonate/${targetUser.id}`);
      toast.success(`Now viewing as ${data.target.name || data.target.email}`);
      // Route residents to /resident, vendors to /vendor
      const home = data.target.role === "vendor" ? "/vendor" : "/resident";
      window.location.href = home;
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Impersonation failed");
      setBusy(false);
    }
  };

  const targetProps = detail?.user?.property_ids || [];
  const changeCount = useMemo(() => {
    if (!detail || !propertyId) return 0;
    const explicit = new Set(detail.grants_by_property?.[propertyId] || []);
    const currentExtras = new Set([...selected].filter((k) => !baseRoleSet.has(k)));
    // Symmetric difference between explicit grants and current extras
    let diff = 0;
    for (const k of currentExtras) if (!explicit.has(k)) diff++;
    for (const k of explicit) if (!currentExtras.has(k)) diff++;
    return diff;
  }, [detail, propertyId, selected, baseRoleSet]);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="bg-card border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="feature-grants-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-400" />
            Feature access · {targetUser.name || targetUser.email}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Grant this user access to specific admin features for a chosen community.
            Base-role features (from <Badge variant="outline" className="ml-1 mr-1 text-[10px]">{targetUser.role}</Badge>)
            are locked ON. Features you don't hold yourself are locked OFF.
          </DialogDescription>
        </DialogHeader>

        {!catalog || !detail ? (
          <div className="py-10 text-center text-sm text-zinc-400">Loading permissions…</div>
        ) : targetProps.length === 0 ? (
          <div className="py-10 text-center text-sm text-zinc-400">
            This user isn't attached to any property yet — add them to a community first.
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-xs uppercase tracking-widest text-zinc-500 shrink-0">Community</div>
              <Select value={propertyId || ""} onValueChange={setPropertyId}>
                <SelectTrigger className="bg-white/[0.02] border-white/10 h-9 w-64"
                                data-testid="feature-grants-property-picker">
                  <SelectValue placeholder="Select community" />
                </SelectTrigger>
                <SelectContent>
                  {targetProps.map((pid) => (
                    <SelectItem key={pid} value={pid}>{pid}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {changeCount > 0 && (
                <Badge variant="outline" className="border-orange-500/40 text-orange-300" data-testid="feature-grants-diff-count">
                  {changeCount} pending change{changeCount === 1 ? "" : "s"}
                </Badge>
              )}
              <div className="flex items-center gap-1.5 ml-2" title="Optional expiry — leave blank for perma-grant">
                <CalendarClock className="h-3.5 w-3.5 text-zinc-500" />
                <input
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  className="bg-white/[0.02] border border-white/10 rounded h-9 px-2 text-xs text-zinc-200"
                  data-testid="feature-grants-expires-input"
                />
                {expiresAt && (
                  <button
                    type="button"
                    onClick={() => setExpiresAt("")}
                    className="text-[10px] text-zinc-500 hover:text-zinc-300"
                    data-testid="feature-grants-expires-clear"
                  >clear</button>
                )}
                {detail?.granted_meta?.[propertyId]?.expired && (
                  <Badge variant="outline" className="border-rose-500/40 text-rose-300 text-[10px]" data-testid="feature-grants-expired-badge">
                    expired
                  </Badge>
                )}
              </div>
              <div className="ml-auto flex items-center gap-2">
                {templates.length > 0 && (
                  <Select onValueChange={applyTemplate}>
                    <SelectTrigger className="bg-white/[0.02] border-emerald-500/30 h-9 w-56 text-emerald-200"
                                    data-testid="feature-grants-template-picker">
                      <Bookmark className="h-3.5 w-3.5 mr-1" />
                      <SelectValue placeholder="Apply template…" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name} <span className="text-[10px] text-zinc-500">({t.features.length} features)</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button variant="outline" size="sm" onClick={() => setShowSaveTemplate((x) => !x)}
                        className="border-white/10 h-9 gap-1.5"
                        data-testid="feature-grants-save-template-btn">
                  <Bookmark className="h-3.5 w-3.5" /> Save as template
                </Button>
              </div>
            </div>

            {showSaveTemplate && (
              <div className="mt-2 flex items-center gap-2 bg-white/[0.02] p-3 rounded-md border border-white/10">
                <Input value={saveTemplateName}
                       onChange={(e) => setSaveTemplateName(e.target.value)}
                       placeholder='Bundle name (e.g. "Front Desk Package")'
                       className="bg-white/[0.03] border-white/10 h-9"
                       data-testid="feature-grants-template-name" />
                <Button size="sm" onClick={saveAsTemplate} disabled={busy || !saveTemplateName.trim()}
                        className="bg-emerald-500 hover:bg-emerald-600 h-9"
                        data-testid="feature-grants-save-template-confirm">
                  Save bundle
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setShowSaveTemplate(false); setSaveTemplateName(""); }}
                        className="h-9 text-xs">
                  Cancel
                </Button>
              </div>
            )}

            <div className="space-y-4 mt-4">
              {Object.entries(grouped).map(([category, features]) => (
                <Card key={category} className="border-white/10 bg-white/[0.02] p-4">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{category}</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6">
                    {features.map((f) => {
                      const locked = baseRoleSet.has(f.key);
                      const notGrantable = !grantable.has(f.key);
                      const checked = selected.has(f.key);
                      const disabled = locked || (notGrantable && !checked);
                      return (
                        <label
                          key={f.key}
                          className={"flex items-start gap-2 cursor-pointer " + (disabled ? "opacity-60 cursor-not-allowed" : "")}
                          data-testid={`feature-row-${f.key}`}
                        >
                          <Checkbox
                            checked={checked}
                            disabled={disabled}
                            onCheckedChange={() => toggle(f)}
                            className="mt-0.5"
                            data-testid={`feature-toggle-${f.key}`}
                          />
                          <div className="text-sm leading-tight">
                            <div className="flex items-center gap-1.5">
                              {f.label}
                              {locked && (
                                <Lock className="h-3 w-3 text-zinc-500" title="Included via base role" />
                              )}
                            </div>
                            <div className="text-[10px] text-zinc-500 font-mono">{f.key}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </Card>
              ))}
            </div>

            <Card className="mt-4 border-white/10 bg-white/[0.02] p-3" data-testid="feature-grants-history-card">
              <button type="button" onClick={() => setHistoryOpen((x) => !x)}
                      className="w-full flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300 transition-colors"
                      data-testid="feature-grants-history-toggle">
                {historyOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRightIcon className="h-3 w-3" />}
                <HistoryIcon className="h-3 w-3" />
                Recent permission changes
                <Badge variant="outline" className="ml-1 border-zinc-700 text-zinc-500 text-[10px]">{history.length}</Badge>
              </button>
              {historyOpen && (
                <div className="mt-3 space-y-2 max-h-40 overflow-y-auto" data-testid="feature-grants-history-list">
                  {history.length === 0 && (
                    <div className="text-xs text-zinc-500 italic">No permission changes recorded yet for this user.</div>
                  )}
                  {history.map((h) => {
                    const isRevoke = h.action === "permissions.revoke";
                    const isTemplate = h.action === "permissions.template_applied";
                    const tone = isRevoke ? "text-rose-300" : isTemplate ? "text-emerald-300" : "text-orange-200";
                    const label = isRevoke ? "Revoked" : isTemplate ? `Applied template "${h.details?.template_name || ""}"` : "Granted";
                    const feats = h.details?.features || [];
                    return (
                      <div key={h.id} className="text-xs text-zinc-400 flex items-start gap-2">
                        <span className={"font-mono text-[10px] w-4 shrink-0 " + tone}>▸</span>
                        <div className="flex-1">
                          <span className={tone + " font-semibold"}>{label}</span>{" "}
                          <span className="text-zinc-500">by {h.actor_name} on {new Date(h.at).toLocaleDateString()}</span>
                          {feats.length > 0 && !isRevoke && (
                            <div className="text-[10px] text-zinc-500 font-mono mt-0.5">{feats.join(", ")}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </>
        )}

        <DialogFooter className="mt-4 gap-2 flex-wrap">
          {["resident", "vendor"].includes(targetUser.role) && (
            <Button variant="outline" onClick={viewAsUser}
                    disabled={busy}
                    className="border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10 gap-1.5"
                    title="Preview the app as this user with these grants"
                    data-testid="feature-grants-view-as">
              <Eye className="h-3.5 w-3.5" /> View as user
            </Button>
          )}
          <Button variant="outline" onClick={revoke}
                  disabled={busy || !propertyId || !(detail?.grants_by_property?.[propertyId]?.length)}
                  className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10"
                  data-testid="feature-grants-revoke">
            Revoke all extras
          </Button>
          <Button variant="outline" onClick={onClose} className="border-white/10">Cancel</Button>
          <Button onClick={save} disabled={busy || !propertyId}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="feature-grants-save">
            {busy ? "Saving…" : "Save grants"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
